import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";
import {
  PAGE_KEYS,
  getFirstAllowedDiscussionPath,
  hasAdminSectionAccess,
  hasEmotiveAccess,
} from "../../utils/pageAccess";
import "./Navbar.scss";

const filterMenuItems = (items, allowedPages) =>
  items.filter(item => item.enabled && allowedPages.includes(item.label));

function Navbar({ user, setUser, allowedPages = [] }) {
  const hasAdminMenu = user?.role === "admin";
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const navigate = useNavigate();
  const navbarRef = useRef(null);
  const notificationsRef = useRef(null);
  const lastPollAtRef = useRef(null);

  const filteredAnalysisMenuItems = useMemo(
    () => {
      const discussionPath = getFirstAllowedDiscussionPath(allowedPages);
      const items = [
        { path: "/posts-search", label: PAGE_KEYS.POSTS_SEARCH, enabled: true },
        { path: "/", label: PAGE_KEYS.DYNAMICS, enabled: true },
        { path: "/pol", label: PAGE_KEYS.USERS, enabled: true },
        { path: "/emotive-analysis", label: PAGE_KEYS.EMOTIVE, enabled: hasEmotiveAccess(allowedPages) },
        { path: discussionPath || "/diss", label: PAGE_KEYS.DISCUSSIONS, enabled: Boolean(discussionPath) },
        { path: "/topics-analysis", label: PAGE_KEYS.THEMATIC, enabled: true },
        { path: "/admin", label: PAGE_KEYS.ADMINISTRATION, enabled: hasAdminMenu && hasAdminSectionAccess(allowedPages) },
      ];
      return filterMenuItems(items, allowedPages);
    },
    [allowedPages, hasAdminMenu]
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("deniedPages");
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      return undefined;
    }

    let isMounted = true;
    lastPollAtRef.current = null;

    const fetchNotifications = async () => {
      try {
        const params = new URLSearchParams({
          user_id: String(user.id),
          read: "false",
          limit: "20",
        });
        if (lastPollAtRef.current) {
          params.set("since", lastPollAtRef.current);
        }
        const response = await apiFetch(`${getAPIUrl()}/tasks/notifications?${params.toString()}`);
        if (!response.ok) return;

        const data = await response.json();
        if (!isMounted) return;

        const incoming = data.notifications || [];
        if (incoming.length > 0) {
          setNotifications((prev) => {
            const byId = new Map(prev.map((item) => [item.notification_id, item]));
            incoming.forEach((item) => byId.set(item.notification_id, item));
            return Array.from(byId.values()).sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
          });
        }
        if (data.polled_at) {
          lastPollAtRef.current = data.polled_at;
        }
      } catch (error) {
        console.error("Ошибка загрузки уведомлений:", error);
      }
    };

    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 15000);
    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markNotificationRead = async (notificationId) => {
    setNotifications((prev) =>
      prev.filter((item) => item.notification_id !== notificationId)
    );

    try {
      await apiFetch(`${getAPIUrl()}/tasks/notifications/${notificationId}/read`, {
        method: "PATCH",
      });
    } catch (error) {
      console.error("Ошибка отметки уведомления:", error);
    }
  };

  const markAllNotificationsRead = async () => {
    if (isMarkingAllRead) return;
    setIsMarkingAllRead(true);
    try {
      const response = await apiFetch(`${getAPIUrl()}/tasks/notifications/read-all`, {
        method: "PATCH",
      });
      if (!response.ok) throw new Error("Не удалось отметить уведомления");
      setNotifications([]);
    } catch (error) {
      console.error("Ошибка массовой отметки уведомлений:", error);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-content">
        <Link to="/" className="navbar-brand">
          <img src="/logo512K.png" alt="Logo" style={{ height: "40px" }} />
        </Link>

        <div className="nav-links">
          {filteredAnalysisMenuItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              {item.label}
            </NavLink>
          ))}


        </div>

        <div className="navbar-actions">
          <div className="notifications" ref={notificationsRef}>
            <button
              type="button"
              className="notifications-toggle"
              onClick={() => setIsNotificationsOpen((value) => !value)}
              title="Уведомления"
              aria-label="Уведомления"
            >
              <FaBell className="notifications-icon" aria-hidden="true" />
              {notifications.length > 0 && (
                <span className="notifications-count">{notifications.length}</span>
              )}
            </button>

            {isNotificationsOpen && (
              <div className="notifications-menu">
                <div className="notifications-list">
                  {notifications.length === 0 ? (
                    <div className="notifications-empty">Новых уведомлений нет</div>
                  ) : (
                    notifications.map((notification) => (
                      <button
                        type="button"
                        key={notification.notification_id}
                        className={`notification-item notification-${notification.type}`}
                        onClick={() => markNotificationRead(notification.notification_id)}
                      >
                        <span className="notification-message">{notification.message}</span>
                        <span className="notification-time">
                          {new Date(notification.created_at).toLocaleString("ru-RU")}
                        </span>
                      </button>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  className="notifications-mark-all"
                  onClick={markAllNotificationsRead}
                  disabled={notifications.length === 0 || isMarkingAllRead}
                >
                  {isMarkingAllRead ? "Отмечаем…" : "Отметить все прочитанными"}
                </button>
              </div>
            )}
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </div>

    </nav>
  );
}

export default Navbar;
